import {
  ChevronLeft,
  ChevronRight,
  Circle,
  CircleCheck,
  Clock,
  Loader,
  LucideUpload,
  Plus,
  ReceiptText,
  Search,
  Sigma,
  SortAsc,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import LoanCard from "../../shared/LoanCard";
import Pagination from "../../shared/Pagination";
import { useNavigate } from "react-router-dom";
import LoanApplicationSkeleton from "../../skeletons/LoanApplicationSkeleton";
import { useFormatAmount } from "../../hooks/useFormatAmount";
import { useFormattedDate } from "../../hooks/useFormattedDate";
import { useQuery } from "react-query";
import { getLoans, getLoanStatistics } from "../../sdk/loans/loans";
import { useError } from "../../context/ErrorProvider";
import ArrearsFilter from "../../components/filter/ArrearsFilter";

const LoanArrears = () => {
  const handlePageChange = (page) => {
    setPage(page);
  };
  const [totalPages, setTotalPages] = useState(0);
  const navigate = useNavigate();
  const formatAmount = useFormatAmount();
  const formattedDate = useFormattedDate();
  const { showError } = useError();
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [fromDate, setFromDate] = useState(new Date("2023-11-23"));
  const [toDate, setToDate] = useState(new Date());
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [selectedLoanOfficer, setSelectedLoanOfficer] = useState("");
  const [loans, setLoans] = useState([]);
  const [openLoanArrearsFilter, setOpenLoanArrearsFilter] = useState(false);

  const toggleOpenLoanArrearsFilter = () => {
    setOpenLoanArrearsFilter(true);
  };

  const closeOpenLoanArrearsFilter = () => {
    setOpenLoanArrearsFilter(false);
  };

  const { data: loanStatistics } = useQuery({
    queryKey: ["loan-statistics"],
    queryFn: async () => {
      const response = await getLoanStatistics();
      return response.data.data;
    },
  });

  const { isFetching } = useQuery({
    queryKey: [
      "loan",
      page,
      limit,
      fromDate ? fromDate.toISOString() : null,
      toDate ? toDate.toISOString() : null,
      search,
      status,
      selectedLoanOfficer,
    ],
    queryFn: async () => {
      const response = await getLoans(
        page,
        limit,
        fromDate ? fromDate.toISOString() : null,
        toDate ? toDate.toISOString() : null,
        search,
        "inarrears",
        selectedLoanOfficer,
      );
      return response.data.data;
    },
    onSuccess: (data) => {
      setLoans(data.data ?? []);
      setTotalPages(data?.totalPages);
      setPage(data.currentPage);
      setLimit(data.limit);
      setTotalItems(data?.totalItems);
    },
    onError: (error) => {
      showError(error?.response?.data?.message || error.message);
    },
  });

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, totalItems);

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case "active":
        return (
          <Circle
            fill="green"
            className="text-green-600 bg-green-100 rounded-full p-1 w-6 h-6"
          />
        );
      case "pending":
        return (
          <Clock className="text-yellow-600 bg-yellow-100 rounded-full p-1 w-6 h-6" />
        );
      case "defaulted":
        return (
          <XCircle className="text-red-600 bg-red-100 rounded-full p-1 w-6 h-6" />
        );
      case "paid":
        return (
          <CircleCheck className="text-green-600 bg-green-100 rounded-full p-1 w-6 h-6" />
        );
      default:
        return (
          <Clock className="text-gray-600 bg-gray-100 rounded-full p-1 w-6 h-6" />
        );
    }
  };
  return (
    <>
      {openLoanArrearsFilter && (
        <ArrearsFilter
          closeFilters={closeOpenLoanArrearsFilter}
          status={status}
          setStatus={setStatus}
          selectedLoanOfficer={selectedLoanOfficer}
          setSelectedLoanOfficer={setSelectedLoanOfficer}
          fromDate={fromDate}
          toDate={toDate}
          setFromDate={setFromDate}
          setToDate={setToDate}
        />
      )}
      {isFetching ? (
        <LoanApplicationSkeleton />
      ) : (
        <div className="pl-[10px]">
          <div className="flex items-center h-[60px] mb-[10px] justify-between">
            <div className="flex items-center gap-[5px]">
              <ChevronLeft color="#000000" size={17} />
              <span
                onClick={() => navigate("/dashboard/loans")}
                className="cursor-pointer"
              >
                Loans
              </span>
              <ChevronRight color="#000000" size={17} />
              <span className="underline font-bold text-[16px] text-[#000000]">
                Loan in Arrears
              </span>
            </div>
          </div>
          <div className="flex items-center my-[20px] gap-[20px]">
            <LoanCard
              icon={<Sigma color="#4B5D67" />}
              label="Total loans"
              value={loanStatistics?.total ?? 0}
              increment="+0.00%"
              padColor="#4B5D67"
            />
            <LoanCard
              icon={<Sigma color="#4B5D67" />}
              label="Total loans in arrears"
              value={loanStatistics?.defaulted ?? 0}
              increment="+0.00%"
              padColor="#4B5D67"
            />
          </div>
          <div className="h-[70px] w-full bg-white flex items-center justify-between px-[10px]">
            <button className="h-[40px] w-[117px] flex items-center justify-center gap-[5px] border border-[#00A676] bg-white rounded-[10px] text-[#00A676] font-medium text-[15px]">
              <Loader size={17} />
              Update
            </button>
            <div className="h-[40px] w-[117px] text-[#5C5C5C] font-medium text-[15px] rounded-[10px] border border-[#F3F3F3] bg-white flex items-center justify-center">
              0 Selected
            </div>
            <div
              onClick={toggleOpenLoanArrearsFilter}
              className="h-[40px] w-[127px] cursor-pointer text-[#5C5C5C] font-medium text-[15px] rounded-[10px] border border-[#F3F3F3] bg-[#FBF5F5] flex items-center justify-center gap-[5px]"
            >
              <SortAsc color="#000000" />
              <span className="text-[#000000]">Filter</span>
              <span className="text-[#000000]">0</span>
            </div>
            <div className="flex items-center border border-gray-300 rounded-md px-3 h-[40px] w-[50%] bg-white">
              <Search className="text-gray-500 w-5 h-5 mr-2" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search loans, borrower name, loan code..."
                className="w-full outline-none text-sm text-gray-700 placeholder-gray-400 bg-transparent"
              />
            </div>
            <p className="text-[#000000] text-[15px] truncate font-normal">
              {loans?.length} Results
            </p>
            <button className="flex items-center bg-[#F3F3F3] border-[0.7px] border-[#A8A8A8] h-[40px] rounded-[30px] px-[20px] justify-center gap-[5px] text-[16px] text-[#5C5C5C]">
              <LucideUpload size={17} />
              <span>Export</span>
            </button>
          </div>
          <div>
            <div className="h-[44px] px-[10px] w-full bg-[#F3F3F3] mb-[10px] flex items-center">
              <div className="w-[5%]">
                <input type="checkbox" name="loan" id="loan" />
              </div>
              <div className="w-[14%] text-[#000] font-medium text-[14px]">
                LOAN ID
              </div>
              <div className="w-[15%] truncate text-[#000] font-medium text-[14px]">
                BORROWER’S NAME
              </div>
              <div className="w-[14%] text-[#000] font-medium text-[14px]">
                LOAN TYPE
              </div>
              <div className="w-[14%] text-[#000] font-medium text-[14px]">
                LOAN AMOUNT
              </div>
              <div className="w-[14%] text-[#000] font-medium text-[14px]">
                BALANCE
              </div>
              <div className="w-[14%] text-[#000] font-medium text-[14px]">
                DUE DATE
              </div>
              <div className="w-[14%] text-[#000] font-medium text-[14px]">
                STATUS
              </div>
            </div>
            {loans.length > 0 ? (
              loans.map((loan, index) => (
                <div
                  key={index}
                  className="h-[40px] px-[10px] w-full bg-[#FFFFFF] mb-[7px] flex items-center"
                >
                  <div className="w-[5%]">
                    <input type="checkbox" name="loan" />
                  </div>
                  <div
                    onClick={() => navigate(`/dashboard/loans/${loan?.id}`)}
                    className="w-[14%] text-[#000000] cursor-pointer font-normal text-[14px]"
                  >
                    #{loan.public_id}
                  </div>
                  <div className="w-[15%] text-[#000] font-normal text-[14px]">
                    {loan?.loanApplication?.customer?.firstname}{" "}
                    {loan?.loanApplication?.customer?.lastname}
                  </div>
                  <div className="w-[14%] text-[#000] font-normal text-[14px]">
                    Salary Loan
                  </div>
                  <div className="w-[14%] text-[#000] font-normal text-[14px]">
                    {formatAmount(loan?.loanApplication?.loanAmount)}
                  </div>
                  <div className="w-[14%] text-[#000] font-normal text-[14px]">
                    {formatAmount(loan?.loanApplication?.repaymentAmount)}
                  </div>
                  <div className="w-[14%] text-[#000] font-normal text-[14px]">
                    {formattedDate(loan?.createdAt)}
                  </div>
                  <div className="w-[14%] text-[#000] font-normal text-[14px] flex items-center gap-2">
                    {getStatusIcon(loan?.status)}
                    <span className="capitalize">{loan.status}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-24 px-6 bg-white border-2 border-gray-100 transition-all">
                {/* Icon Container with soft ZipGo Green glow */}
                <div className="relative mb-8">
                  <div className="absolute inset-0 bg-[#2A8C52] blur-[40px] opacity-10 rounded-full scale-150" />

                  <div className="relative w-24 h-24 bg-white rounded-3xl flex items-center justify-center border border-gray-100 shadow-2xl shadow-gray-200/40">
                    {/* ReceiptText or Landmark works great for Loans */}
                    <ReceiptText
                      size={40}
                      className="text-[#2A8C52] opacity-90"
                    />

                    {/* Floating 'plus' badge */}
                    <div className="absolute -top-2 -right-2 w-9 h-9 bg-[#2A8C52] rounded-2xl flex items-center justify-center border-4 border-white shadow-lg">
                      <Plus size={18} className="text-white" strokeWidth={3} />
                    </div>
                  </div>
                </div>

                {/* Textual Information */}
                <div className="max-w-md text-center">
                  <h3 className="text-gray-900 font-extrabold text-2xl tracking-tight">
                    No Loan Records Found
                  </h3>
                  <p className="text-gray-500 mt-3 text-[15px] leading-relaxed">
                    Your loan directory is currently empty. You can start by
                    issuing a new loan to an existing borrower or adjust your
                    current search filters.
                  </p>
                </div>
              </div>
            )}
          </div>
          {totalPages > 0 && (
            <div className="h-[50px] mb-[20px] flex items-center justify-between">
              <div className="flex items-center gap-[10px]">
                <p className="text-[15px] text-[#5C5C5C] font-normal">
                  {start}–{end} of {totalItems}
                </p>
                <p className="text-[15px] text-[#5C5C5C] font-normal">
                  Row/Page:
                </p>
                <p className="text-[15px] text-[#5C5C5C] font-normal">
                  {page}/{totalPages}
                </p>
                <select
                  className="h-[35px] w-[100px] px-[10px] text-[12px]"
                  name="row"
                  id="row"
                  value={limit}
                  onChange={(e) => setLimit(e.target.value)}
                >
                  <option value="10">10 items</option>
                  <option value="20">20 items</option>
                  <option value="25">25 items</option>
                </select>
              </div>
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default LoanArrears;
